import { Box, Grid2 } from '@mui/material';

import { useNavigate, useParams } from 'react-router';
import {
  useGetFacilityDetailsById,
  useGetFacilityMetaData,
} from '../../API/hooks/FacilityHook';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import React, { useEffect, useState } from 'react';
import facilityInitialValues from './facilitiesInitialValues';
import { NEW_FACILITY_ROUTE_PATH } from '../../routers/routePaths';

import { CustomDivider } from '@qler-view/shared_lib/src';
import './facility.css';
import CustomButton from '@ui-components/Button/CustomButton';
import { hasRole } from '../login/storageUtility';

interface FacilityDetailsProps {
  id?: string;
}

const FacitilityView: React.FC<FacilityDetailsProps> = ({ id }) => {
  const navigate = useNavigate();
  const userId = localStorage.getItem('userId') ?? '0';
  const { facilityId: paramId } = useParams<{ facilityId?: string }>();
  const resolvedFacilityId = id || paramId;
  const { facilityMetaData } = useGetFacilityMetaData(userId);
  const [initialValues, setInitialValues] = useState(facilityInitialValues);
  const { FacilityDetailsById, isLoading } = useGetFacilityDetailsById(
    userId,
    Number(resolvedFacilityId)
  );

  useEffect(() => {
    if (FacilityDetailsById && !isLoading) {
      setInitialValues({
        facilityInfo: FacilityDetailsById.facilityInfo,
        contactDetails: FacilityDetailsById.contactDetails,
        itSystemDetails: FacilityDetailsById.itSystemDetails,
        address: FacilityDetailsById.address,
        description: FacilityDetailsById.description,
      });
    }
  }, [FacilityDetailsById]);

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CustomTypography
          id="loading-text"
          variant="h5"
          className="text-align-center pt-32"
          color="textDisabled"
        >
          Loading...
        </CustomTypography>
      </Box>
    );
  }

  const handleEditFacilityInfo = (facilityId: number) => {
    navigate(NEW_FACILITY_ROUTE_PATH + `/${facilityId}`);
  };

  const displayValueOrNA = (value: number | null | undefined): string => {
    return value ? String(value) : 'N/A';
  };

  const displayValueOrEmpty = (value: string | null | undefined): string => {
    return value === '0' ? 'N/A' : String(value);
  };

  const customLinkTypeId = facilityMetaData?.videoConferenceTypeId?.find(
    (item) => item.name.toLowerCase() === 'custom link'
  )?.id;
  return (
    <Box>
      <Box className="pb-20">
        <Grid2 container>
          <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
            <Box className=" flex-justify-space-between align-items-baseline">
              <Box className="text-align-left display-flex">
                <CustomTypography variant="h4" id="add-new-facility-text">
                  View Facility - ({initialValues.facilityInfo.name})
                </CustomTypography>
              </Box>
              {hasRole(['ops-admin']) && (
                <Box>
                  <CustomButton
                    id="save-button"
                    onClick={() => {
                      handleEditFacilityInfo(initialValues.facilityInfo.id);
                    }}
                    variant="contained"
                  >
                    Edit
                  </CustomButton>
                </Box>
              )}
            </Box>
          </Grid2>
        </Grid2>
      </Box>
      <Box className="pb-20">
        <Grid2 container>
          <Box className="form-background p-24">
            <Grid2 container>
              <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <CustomTypography
                  variant="h5"
                  className="text-align-left pb-32"
                  id="facility-info-text"
                >
                  Facility Information
                </CustomTypography>
              </Grid2>
            </Grid2>
            <Box className="display-flex">
              <Grid2 container spacing={4} className="width-80">
                <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                  <CustomTypography
                    id="facility-name-label"
                    variant="body3"
                    color="textDisabled"
                    className="text-align-left pb-12"
                  >
                    Facility Name
                  </CustomTypography>
                  <CustomTypography
                    id="facility-name-label"
                    variant="body3"
                    color="textPrimary"
                    className="text-align-left"
                  >
                    {initialValues.facilityInfo.name || 'N/A'}
                  </CustomTypography>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                  <CustomTypography
                    id="facility-alias-label"
                    variant="body3"
                    color="textDisabled"
                    className="text-align-left pb-12"
                  >
                    Alias
                  </CustomTypography>
                  <CustomTypography
                    id="facility-alias-value"
                    variant="body3"
                    color="textPrimary"
                    className="text-align-left"
                  >
                    {initialValues.facilityInfo.alias || 'N/A'}
                  </CustomTypography>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                  <CustomTypography
                    id="facility-status-label"
                    variant="body3"
                    color="textDisabled"
                    className="text-align-left pb-12"
                  >
                    Status
                  </CustomTypography>
                  <Box className="text-align-left">
                    {initialValues.facilityInfo.status ? (
                      <Box className="display-flex align-items-baseline">
                        <Box component="span" className="status-active-dot" />
                        <CustomTypography
                          id="live-on-qler-value"
                          variant="body3"
                          color="textPrimary"
                          className="text-align-left pl-4"
                        >
                          {' '}
                          Active
                        </CustomTypography>
                      </Box>
                    ) : (
                      <Box className="display-flex align-items-baseline">
                        <Box component="span" className="status-inactive-dot" />
                        <CustomTypography
                          id="live-on-qler-value"
                          variant="body3"
                          color="textPrimary"
                          className="text-align-left pl-4"
                        >
                          {' '}
                          Inactive
                        </CustomTypography>
                      </Box>
                    )}
                  </Box>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                  <CustomTypography
                    id="partner-label"
                    variant="body3"
                    color="textDisabled"
                    className="text-align-left pb-12"
                  >
                    Partner
                  </CustomTypography>
                  <CustomTypography
                    id="partner-value"
                    variant="body3"
                    color="textPrimary"
                    className="text-align-left"
                  >
                    {facilityMetaData?.partners
                      .find(
                        (facilityType) =>
                          facilityType.id ===
                          initialValues.facilityInfo.partnerId
                      )
                      ?.value.name.toLocaleUpperCase() ?? 'N/A'}
                  </CustomTypography>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                  <CustomTypography
                    id="facility-short-name-label"
                    variant="body3"
                    color="textDisabled"
                    className="text-align-left pb-12"
                  >
                    Facility Short Name
                  </CustomTypography>
                  <CustomTypography
                    id="facility-short-name-value"
                    variant="body3"
                    color="textPrimary"
                    className="text-align-left"
                  >
                    {initialValues.facilityInfo.shortName || 'N/A'}
                  </CustomTypography>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                  <CustomTypography
                    id="facility-services-provided-label"
                    variant="body3"
                    color="textDisabled"
                    className="text-align-left pb-12"
                  >
                    Services Provided
                  </CustomTypography>
                  <CustomTypography
                    id="facility-services-provided-value"
                    variant="body3"
                    color="textPrimary"
                    className="text-align-left"
                  >
                    {initialValues.facilityInfo.servicesProvided.length === 0
                      ? 'N/A'
                      : initialValues.facilityInfo.servicesProvided
                          .map((service) => service.name)
                          .join(', ')}
                  </CustomTypography>
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                  <CustomTypography
                    id="facility-type-label"
                    variant="body3"
                    color="textDisabled"
                    className="text-align-left pb-12"
                  >
                    Facility Type
                  </CustomTypography>
                  <CustomTypography
                    id="facility-type-value"
                    variant="body3"
                    color="textPrimary"
                    className="text-align-left"
                  >
                    {facilityMetaData?.facilityTypes.find(
                      (facilityType) =>
                        facilityType.id ===
                        initialValues.facilityInfo.facilityTypeId
                    )?.name ?? 'N/A'}
                  </CustomTypography>
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                  <CustomTypography
                    id="facility-website-label"
                    variant="body3"
                    color="textDisabled"
                    className="text-align-left pb-12"
                  >
                    Website
                  </CustomTypography>
                  <CustomTypography
                    id="facility-website-value"
                    variant="body3"
                    color="textPrimary"
                    className="text-align-left"
                  >
                    {initialValues.facilityInfo.website || 'N/A'}
                  </CustomTypography>
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                  <CustomTypography
                    id="video-conferencing-label"
                    variant="body3"
                    color="textDisabled"
                    className="text-align-left pb-12"
                  >
                    Video Conferencing
                  </CustomTypography>
                  <CustomTypography
                    id="video-conferencing-value"
                    variant="body3"
                    color="textPrimary"
                    className="text-align-left"
                  >
                    {facilityMetaData?.videoConferenceTypeId.find(
                      (item) =>
                        item.id ===
                        initialValues.facilityInfo.videoConferenceTypeId
                    )?.name ?? 'N/A'}
                  </CustomTypography>
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                  <CustomTypography
                    id="intake-form-label"
                    variant="body3"
                    color="textDisabled"
                    className="text-align-left pb-12"
                  >
                    Intake Form
                  </CustomTypography>
                  <CustomTypography
                    id="intake-form-value"
                    variant="body3"
                    color="textPrimary"
                    className="text-align-left"
                  >
                    {facilityMetaData?.intakeForms.find(
                      (item) =>
                        item.id === initialValues.facilityInfo.intakeFormId
                    )?.name ?? 'N/A'}
                  </CustomTypography>
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                  <CustomTypography
                    id="minimum-age-label"
                    variant="body3"
                    color="textDisabled"
                    className="text-align-left pb-12"
                  >
                    Minimum Age Required
                  </CustomTypography>
                  <CustomTypography
                    id="minimum-age-value"
                    variant="body3"
                    color="textPrimary"
                    className="text-align-left "
                  >
                    {displayValueOrNA(
                      Number.parseInt(
                        initialValues.facilityInfo.minimumAgeRequired
                      )
                    )}
                  </CustomTypography>
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                  <CustomTypography
                    id="active-facility-since-label"
                    variant="body3"
                    color="textDisabled"
                    className="text-align-left pb-12"
                  >
                    Active Facility Since
                  </CustomTypography>
                  <CustomTypography
                    id="active-facility-since-value"
                    variant="body3"
                    color="textPrimary"
                    className="text-align-left"
                  >
                    {initialValues.facilityInfo.activeFacilitySince || 'N/A'}
                  </CustomTypography>
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                  <CustomTypography
                    id="facility-termination-label"
                    variant="body3"
                    color="textDisabled"
                    className="text-align-left pb-12"
                  >
                    Termination Date
                  </CustomTypography>
                  <CustomTypography
                    id="facility-termination-value"
                    variant="body3"
                    color="textPrimary"
                    className="text-align-left"
                  >
                    {initialValues.facilityInfo.terminationDate || 'N/A'}
                  </CustomTypography>
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                  <CustomTypography
                    id="facility-assigned-to-label"
                    variant="body3"
                    color="textDisabled"
                    className="text-align-left pb-12"
                  >
                    Assigned To
                  </CustomTypography>
                  <CustomTypography
                    id="facility-assigned-to-value"
                    variant="body3"
                    color="textPrimary"
                    className="text-align-left "
                  >
                    {facilityMetaData?.assignedTo.find(
                      (item) =>
                        item.id === initialValues.facilityInfo.assignedTo
                    )?.name ?? 'N/A'}
                  </CustomTypography>
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                  <CustomTypography
                    id="facility-Hide-PHI-label"
                    variant="body3"
                    color="textDisabled"
                    className="text-align-left pb-12"
                  >
                    Hide PHI
                  </CustomTypography>
                  <Box className="text-align-left">
                    {initialValues.facilityInfo.isHidePhiAfterCompletion ? (
                      <Box className="display-flex align-items-baseline">
                        <Box component="span" className="status-active-dot" />
                        <CustomTypography
                          id="facility-Hide-PHI-value-On"
                          variant="body3"
                          color="textPrimary"
                          className="text-align-left pl-4"
                        >
                          {' '}
                          On
                        </CustomTypography>
                      </Box>
                    ) : (
                      <Box className="display-flex align-items-baseline">
                        <Box component="span" className="status-inactive-dot" />
                        <CustomTypography
                          id="facility-Hide-PHI-value-Off"
                          variant="body3"
                          color="textPrimary"
                          className="text-align-left pl-4"
                        >
                          {' '}
                          Off
                        </CustomTypography>
                      </Box>
                    )}
                  </Box>
                </Grid2>

                {initialValues.facilityInfo.videoConferenceTypeId ===
                  customLinkTypeId && (
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="custom-link-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      Custom Link
                    </CustomTypography>
                    <CustomTypography
                      id="custom-link-value"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left"
                    >
                      {initialValues.facilityInfo.customLink || 'N/A'}
                    </CustomTypography>
                  </Grid2>
                )}
              </Grid2>
            </Box>
            <Box className="pt-32">
              <CustomDivider />
            </Box>
            <Box className="pt-32">
              <Grid2 container spacing={4} className="width-80">
                <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                  <CustomTypography
                    variant="h5"
                    className="text-align-left pb-32"
                    id="facility-key-contacts-text"
                  >
                    Key Phone Numbers and Email
                  </CustomTypography>
                </Grid2>
              </Grid2>
              <Grid2 container spacing={4} className="width-80">
                <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                  <CustomTypography
                    id="facility-primary-phone-label"
                    variant="body3"
                    color="textDisabled"
                    className="text-align-left pb-12"
                  >
                    Primary Phone
                  </CustomTypography>
                  <CustomTypography
                    id="facility-primary-phone-value"
                    variant="body3"
                    color="textPrimary"
                    className="text-align-left"
                  >
                    {initialValues.contactDetails.primaryPhone || 'N/A'}
                  </CustomTypography>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                  <CustomTypography
                    id="facility-IT-help-desk-phone-label"
                    variant="body3"
                    color="textDisabled"
                    className="text-align-left pb-12"
                  >
                    IT Help Desk Phone
                  </CustomTypography>

                  <CustomTypography
                    id="facility-IT-help-desk-phone-value"
                    variant="body3"
                    color="textPrimary"
                    className="text-align-left"
                  >
                    {initialValues.contactDetails.itHelpDeskPhone || 'N/A'}
                  </CustomTypography>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                  <CustomTypography
                    id="facility-IT-help-desk-label"
                    variant="body3"
                    color="textDisabled"
                    className="text-align-left pb-12"
                  >
                    IT Help Desk Email
                  </CustomTypography>

                  <CustomTypography
                    id="facility-IT-help-desk-value"
                    variant="body3"
                    color="textPrimary"
                    className="text-align-left"
                  >
                    {initialValues.contactDetails.itHelpDeskEmail || 'N/A'}
                  </CustomTypography>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                  <CustomTypography
                    id="facility-ed-phone-label"
                    variant="body3"
                    color="textDisabled"
                    className="text-align-left pb-12"
                  >
                    ED Phone
                  </CustomTypography>
                  <CustomTypography
                    id="facility-ed-phone-value"
                    variant="body3"
                    color="textPrimary"
                    className="text-align-left"
                  >
                    {initialValues.contactDetails.edPhone || 'N/A'}
                  </CustomTypography>
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                  <CustomTypography
                    id="facility-icu-phone-label"
                    variant="body3"
                    color="textDisabled"
                    className="text-align-left pb-12"
                  >
                    ICU Phone
                  </CustomTypography>
                  <CustomTypography
                    id="facility-icu-phone-value"
                    variant="body3"
                    color="textPrimary"
                    className="text-align-left"
                  >
                    {initialValues.contactDetails.icuPhone || 'N/A'}
                  </CustomTypography>
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                  <CustomTypography
                    id="facility-med-surg-phone-label"
                    variant="body3"
                    color="textDisabled"
                    className="text-align-left pb-12"
                  >
                    Med Surg Phone
                  </CustomTypography>

                  <CustomTypography
                    id="facility-med-surg-phone-value"
                    variant="body3"
                    color="textPrimary"
                    className="text-align-left"
                  >
                    {initialValues.contactDetails.medSurgPhone || 'N/A'}
                  </CustomTypography>
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                  <CustomTypography
                    id="facility-secure-fax-label"
                    variant="body3"
                    color="textDisabled"
                    className="text-align-left pb-12"
                  >
                    Secure Fax
                  </CustomTypography>
                  <CustomTypography
                    id="facility-secure-fax-value"
                    variant="body3"
                    color="textPrimary"
                    className="text-align-left"
                  >
                    {initialValues.contactDetails.secureFax || 'N/A'}
                  </CustomTypography>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                  <CustomTypography
                    id="facility-other-fax-label"
                    variant="body3"
                    color="textDisabled"
                    className="text-align-left pb-12"
                  >
                    Other Fax
                  </CustomTypography>

                  <CustomTypography
                    id="facility-other-fax-value"
                    variant="body3"
                    color="textPrimary"
                    className="text-align-left"
                  >
                    {initialValues.contactDetails.otherFax || 'N/A'}
                  </CustomTypography>
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                  <CustomTypography
                    id="other--label"
                    variant="body3"
                    color="textDisabled"
                    className="text-align-left pb-12"
                  >
                    Other
                  </CustomTypography>
                  <CustomTypography
                    id="other--value"
                    variant="body3"
                    color="textPrimary"
                    className="text-align-left"
                  >
                    {initialValues.contactDetails.other || 'N/A'}
                  </CustomTypography>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                  <CustomTypography
                    id="other-fax-description-label"
                    variant="body3"
                    color="textDisabled"
                    className="text-align-left pb-12"
                  >
                    Other Fax Description
                  </CustomTypography>
                  <CustomTypography
                    id="other-fax-description-label"
                    variant="body3"
                    color="textPrimary"
                    className="text-align-left"
                  >
                    {initialValues.contactDetails.otherFaxDescription || 'N/A'}
                  </CustomTypography>
                </Grid2>
              </Grid2>

              <Box className="pt-32">
                <CustomDivider />
              </Box>
              <Box className="pt-32">
                <Grid2 container spacing={4} className="width-80">
                  <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                    <CustomTypography
                      variant="h5"
                      className="text-align-left pb-32"
                      id="facility-it-system-text"
                    >
                      IT System Details
                    </CustomTypography>
                  </Grid2>
                </Grid2>
                <Grid2 container spacing={4} className="width-80">
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="facility-network-access-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      Network Access Type
                    </CustomTypography>
                    <CustomTypography
                      id="facility-network-access-value"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left"
                    >
                      {initialValues.itSystemDetails.networkAccessType || 'N/A'}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="facility-emr-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      EMR
                    </CustomTypography>
                    <CustomTypography
                      id="facility-emr-value"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left "
                    >
                      {initialValues.itSystemDetails.emr || 'N/A'}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="epic-context-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      EPIC Context
                    </CustomTypography>
                    <CustomTypography
                      id="epic-context-value"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left"
                    >
                      {initialValues.itSystemDetails.epicContext || 'N/A'}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="facility-network-access-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      Network Access URL
                    </CustomTypography>
                    <CustomTypography
                      id="facility-network-access-value"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left"
                    >
                      {initialValues.itSystemDetails.networkAccessUrl || 'N/A'}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="facility-documentation-in-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      Documentation In
                    </CustomTypography>
                    <CustomTypography
                      id="facility-documentation-in-value"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left"
                    >
                      {initialValues.itSystemDetails.documentationIn || 'N/A'}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="training-requirements-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      Training Requirements
                    </CustomTypography>
                    <CustomTypography
                      id="training-requirements-value"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left"
                    >
                      {initialValues.itSystemDetails.trainingRequirements ||
                        'N/A'}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="facility-vpn-gateway-software-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      VPN Gateway Software
                    </CustomTypography>
                    <CustomTypography
                      id="facility-vpn-gateway-software-value"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left"
                    >
                      {initialValues.itSystemDetails.vpnGatewaySoftware ||
                        'N/A'}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="facility-telemedicine-platform-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      Telemedicine Platform
                    </CustomTypography>
                    <CustomTypography
                      id="facility-telemedicine-platform-value"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left"
                    >
                      {initialValues.itSystemDetails.telemedicinePlatform ||
                        'N/A'}
                    </CustomTypography>
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="facility-telemedicine-platform-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      Portal UN/PW
                    </CustomTypography>
                    <CustomTypography
                      id="facility-portal-unpw-value"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left"
                    >
                      {initialValues.itSystemDetails.portalUnPw || 'N/A'}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="facility-mfa-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      MFA
                    </CustomTypography>
                    <CustomTypography
                      id="facility-mfa-value"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left"
                    >
                      {initialValues.itSystemDetails.mfa || 'N/A'}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="facility-telemedicine-platform-url-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      Telemedicine Platform URL
                    </CustomTypography>
                    <CustomTypography
                      id="facility-telemedicine-platform-url-value"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left"
                    >
                      {initialValues.itSystemDetails.telemedicinePlatformUrl ||
                        'N/A'}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                    <CustomTypography
                      id="devices-utilized-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      Devices Utilized
                    </CustomTypography>
                    <CustomTypography
                      id="devices-utilized-value"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left"
                    >
                      {initialValues.itSystemDetails.devicesUtilized || 'N/A'}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                    <CustomTypography
                      id="facility-it-notes-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      IT Notes
                    </CustomTypography>
                    <CustomTypography
                      id="facility-it-notes-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left"
                    >
                      {initialValues.itSystemDetails.itNotes || 'N/A'}
                    </CustomTypography>
                  </Grid2>
                </Grid2>
              </Box>
              <Box className="pt-32">
                <CustomDivider />
              </Box>

              <Box className="pt-32">
                <Grid2 container spacing={4} className="width-80">
                  <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                    <CustomTypography
                      variant="h5"
                      className="text-align-left pb-32"
                      id="address-details-text"
                    >
                      Address Details
                    </CustomTypography>
                  </Grid2>
                </Grid2>
                <Grid2 container spacing={4} className="width-80">
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="facility-physical-address-line-1-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      Address Line 1
                    </CustomTypography>
                    <CustomTypography
                      id="txt-facility-physical-address-line-1-value"
                      className="text-align-left"
                      variant="body3"
                      color="textPrimary"
                    >
                      {initialValues.address.addressLine1 || 'N/A'}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="facility-physical-city-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      City
                    </CustomTypography>
                    <CustomTypography
                      id="txt-facility-physical-city-value"
                      className="text-align-left"
                      variant="body3"
                      color="textPrimary"
                    >
                      {initialValues.address.city || 'N/A'}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="facility-physical-postal-code-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      Postal Code
                    </CustomTypography>
                    <CustomTypography
                      id="facility-physical-postal-code-value"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left"
                    >
                      {initialValues.address.postalCode || 'N/A'}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="facility-physical-address-line-2-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      Address Line 2
                    </CustomTypography>
                    <CustomTypography
                      id="txt-facility-physical-address-line-2-value"
                      className="text-align-left"
                      variant="body3"
                      color="textPrimary"
                    >
                      {initialValues.address.addressLine2 || 'N/A'}
                    </CustomTypography>
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="facility-physical-state-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      State
                    </CustomTypography>
                    <CustomTypography
                      id="facility-physical-state-value"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left"
                    >
                      {displayValueOrEmpty(initialValues.address.state)}
                    </CustomTypography>
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="facility-physical-country-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      County
                    </CustomTypography>
                    <CustomTypography
                      id="facility-physical-country-value"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left "
                    >
                      {initialValues.address.county || 'N/A'}
                    </CustomTypography>
                  </Grid2>
                </Grid2>
              </Box>
              <Box className="pt-32">
                <CustomDivider />
              </Box>
              <Box className="pt-32">
                <Grid2 container spacing={4} className="width-80">
                  <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                    <CustomTypography
                      variant="h5"
                      className="text-align-left pb-32"
                      id="facility-description-details-text"
                    >
                      Additional Details
                    </CustomTypography>
                  </Grid2>
                </Grid2>
                <Grid2 container spacing={4} className="width-80">
                  <Grid2 size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}>
                    <CustomTypography
                      id="facility-description-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      Description
                    </CustomTypography>
                    <CustomTypography
                      id="facility-description-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left"
                    >
                      {initialValues.description || 'N/A'}
                    </CustomTypography>
                  </Grid2>
                </Grid2>
              </Box>
            </Box>
          </Box>
        </Grid2>
      </Box>
    </Box>
  );
};

export default FacitilityView;
